<script setup lang="ts">
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { computed, ref } from 'vue'
import { Icon } from '@iconify/vue'
import { getAllCustomers } from '@/services/core.service'
import type { ICustomer } from '@/types/core/Customer'
import { getCustomerDisplayName } from '@/helpers/core/customers'
import { NotifyFail } from '@/helpers/core/notifications'

const emit = defineEmits<{ change: [customer: ICustomer | undefined] }>()

const props = withDefaults(defineProps<{ disabled?: boolean; placeholder?: string }>(), {
  disabled: false,
  placeholder: 'Customer',
})

const selectedCustomer = defineModel<ICustomer | undefined>({ required: false })

// Customer search state
const customerSearchLoading = ref<boolean>(false)
const customerSearchResults = ref<OptionType[]>([])

// Always keep the selected customer present as an option so el-select-v2 can resolve its
// display name — including when it's set programmatically (e.g. a preset in a dialog).
const customerOptions = computed<OptionType[]>(() => {
  const options = [...customerSearchResults.value]
  const selected = selectedCustomer.value
  if (selected && !options.some((option) => (option.value as ICustomer)?.id === selected.id)) {
    options.push({ value: selected, label: getCustomerDisplayName(selected) })
  }
  return options
})

/**
 * Customer `ElSelectV2` remote search method.
 *
 * @param query customer filter (search query).
 */
const searchCustomers = async (query: string) => {
  if (query == '') return

  try {
    customerSearchLoading.value = true
    const results = await getAllCustomers({
      fragment: query,
      page: 1,
      per_page: 10,
    })
    customerSearchResults.value = results.data.map((customer: ICustomer) => {
      return {
        label: getCustomerDisplayName(customer),
        value: customer,
      }
    })
  } catch {
    NotifyFail('Customer search failed')
  } finally {
    customerSearchLoading.value = false
  }
}

const onChange = (val?: ICustomer) => {
  emit('change', val)
}
</script>

<template>
  <el-select-v2
    v-model="selectedCustomer"
    data-testid="customer-select"
    :options="customerOptions"
    value-key="id"
    remote
    :remote-method="searchCustomers"
    :loading="customerSearchLoading"
    no-data-text="No data"
    :placeholder="props.placeholder"
    clearable
    filterable
    :disabled="disabled"
    @change="onChange"
  >
    <template #loading>
      <div class="remote-search--loading">
        <Icon icon="eos-icons:three-dots-loading" height="20" /></div
    ></template>
  </el-select-v2>
</template>

<style scoped lang="scss">
.remote-search--loading {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
